import React, { useState } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaMugHot,
  FaLeaf,
} from "react-icons/fa";
import SettingsSectionShell, {
  SettingsSubgroupHeader,
} from "./SettingsSectionShell";
import SettingsFieldCard from "./SettingsFieldCard";
import valuesEqual from "./hooks/valuesEqual";
import { applyLabelOverride } from "./labelOverrides";
import AutoMixerCard from "./AutoMixerCard";

const COLOR = "amber";

const MODE_LABELS = {
  fast: "Hızlı",
  normal: "Normal",
  gourmet: "Gurme",
};

const MODE_FIELD_ORDER = [
  "firstWater",
  "cookingTemperature",
  "maxCookingTime",
  "serviceDown",
  "halfPowerTemperature",
  "halfPowerPercentage",
  "mixerSpeed",
  "firstMixerTime",
  "halfPowerMixerRatio",
];

// Pot turunu (TFC vs TC) bilinen alan adlarindan tespit et
const TFC_FIELD_KEYS = new Set([
  "potCalibrationTime",
  "massScale",
  "autoBrewMassInTheAir",
  "autoWaterMassInTheAir",
]);

const TC_FLAT_FIELD_KEYS = new Set([
  "cleaningWaterLevel",
  "cupDisabled",
  "cupAlready",
  "defaultPowerMode",
  "defaultCup",
]);

const orderedKeys = (obj, preferred = []) => {
  if (!obj) return [];
  const all = Object.keys(obj);
  const known = preferred.filter((k) => k in obj);
  const extras = all.filter((k) => !preferred.includes(k)).sort();
  return [...known, ...extras];
};

const isSettingField = (val) =>
  val && typeof val === "object" && "type" in val && "label" in val;

// Otomatik karıştırma açıkken tablette gizlenen karıştırıcıya bağlı alanlar
// (BeverageMachine cupConstants MIXER_DEPENDENT_KEYS).
const MIXER_DEPENDENT_KEYS = new Set(["mixerSpeed", "halfPowerMixerRatio"]);
const AUTO_MIXER_KEYS = new Set(["autoMixer", "autoMixerParams"]);

function ModeBlock({
  mode,
  title,
  modeData,
  originalModeData,
  cupId,
  potIndex,
  emit,
  showApplyAll,
}) {
  const [applyAll, setApplyAll] = useState(false);
  if (!modeData) return null;
  const autoMixerOn =
    isSettingField(modeData.autoMixer) && modeData.autoMixer.value !== false;
  const keys = orderedKeys(modeData, MODE_FIELD_ORDER).filter(
    (k) =>
      isSettingField(modeData[k]) &&
      !AUTO_MIXER_KEYS.has(k) &&
      !(autoMixerOn && MIXER_DEPENDENT_KEYS.has(k)),
  );
  const hasAutoMixer =
    isSettingField(modeData.autoMixer) ||
    isSettingField(modeData.autoMixerParams);
  if (keys.length === 0 && !hasAutoMixer) return null;

  const emitField = (key, value, allPots = applyAll) =>
    emit({ cupId, mode, key, value }, { allPots });

  const autoMixerChanged = ["autoMixer", "autoMixerParams"].some((k) => {
    const orig = originalModeData?.[k]?.value;
    return (
      originalModeData?.[k] !== undefined &&
      !valuesEqual(modeData[k]?.value ?? null, orig ?? null)
    );
  });

  const handleApplyAll = (checked) => {
    setApplyAll(checked);
    if (!checked) return;
    // Tabletteki gibi: kutu işaretlenince bu fincanın mevcut otomatik
    // karıştırma ayarı tüm gözlere yazılır (kaydetme onayıyla).
    if (isSettingField(modeData.autoMixer)) {
      emitField("autoMixer", modeData.autoMixer.value !== false, true);
    }
    if (isSettingField(modeData.autoMixerParams)) {
      emitField("autoMixerParams", modeData.autoMixerParams.value ?? null, true);
    }
  };

  return (
    <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-2.5">
      <div className="text-[11px] font-bold text-amber-700 uppercase tracking-wide mb-2">
        {title || MODE_LABELS[mode] || mode}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {hasAutoMixer && (
          <AutoMixerCard
            idPrefix={`pot${potIndex}-${cupId}-${mode}`}
            autoMixer={
              isSettingField(modeData.autoMixer) ? modeData.autoMixer : null
            }
            autoMixerParams={
              isSettingField(modeData.autoMixerParams)
                ? modeData.autoMixerParams
                : null
            }
            changed={autoMixerChanged}
            cookingTemperature={modeData.cookingTemperature?.value}
            onChange={(key, value) => emitField(key, value)}
            showApplyAll={showApplyAll}
            applyAll={applyAll}
            onApplyAllChange={handleApplyAll}
          />
        )}
        {keys.map((k) => {
          const setting = modeData[k];
          const orig = originalModeData?.[k]?.value;
          const isChanged =
            orig !== undefined && !valuesEqual(setting.value, orig);
          return (
            <SettingsFieldCard
              key={k}
              setting={setting}
              onChange={(value) => emitField(k, value, false)}
              isChanged={isChanged}
              originalValue={orig}
              color={COLOR}
            />
          );
        })}
      </div>
    </div>
  );
}

function CupBlock({ cup, originalCup, potIndex, emit, scope, showApplyAll }) {
  const [open, setOpen] = useState(false);
  const modes = cup.modes || {};
  // Basit / Standart kapsamda tablette mod ayrımı yok: Normal gösterilir,
  // her değişiklik 3 moda birden yazılır (emit genişletir).
  const collapsed = scope === "basit" || scope === "standart";
  const modeKeys = collapsed
    ? modes.normal
      ? ["normal"]
      : []
    : orderedKeys(modes, ["fast", "normal", "gourmet"]);
  const modeFieldCount = modeKeys.reduce(
    (acc, m) =>
      acc +
      orderedKeys(modes[m], MODE_FIELD_ORDER).filter((k) =>
        isSettingField(modes[m]?.[k]),
      ).length,
    0,
  );
  const cupLevelFieldCount = isSettingField(cup.waterLevel) ? 1 : 0;
  const total = cupLevelFieldCount + modeFieldCount;

  return (
    <div className="rounded-xl border border-amber-200 bg-white mb-2 last:mb-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 flex items-center gap-2 text-left bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 rounded-t-xl"
      >
        {open ? (
          <FaChevronDown size={11} className="text-amber-700" />
        ) : (
          <FaChevronRight size={11} className="text-amber-700" />
        )}
        <FaMugHot className="text-amber-600" size={12} />
        <span className="text-sm font-semibold text-amber-900">
          {cup.name || cup.id || "Fincan"}
        </span>
        <span className="ml-auto text-[11px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-md border border-amber-200">
          {total} alan
        </span>
      </button>
      {open && (
        <div className="p-3 space-y-2.5">
          {isSettingField(cup.waterLevel) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              <SettingsFieldCard
                setting={cup.waterLevel}
                onChange={(value) =>
                  emit({ cupId: cup.id, key: "waterLevel", value })
                }
                isChanged={
                  originalCup?.waterLevel?.value !== undefined &&
                  !valuesEqual(
                    cup.waterLevel.value,
                    originalCup.waterLevel.value,
                  )
                }
                originalValue={originalCup?.waterLevel?.value}
                color={COLOR}
              />
            </div>
          )}
          {modeKeys.map((m) => (
            <ModeBlock
              key={m}
              mode={m}
              title={collapsed ? "Tüm pişirme modları" : undefined}
              modeData={modes[m]}
              originalModeData={originalCup?.modes?.[m]}
              potIndex={potIndex}
              cupId={cup.id}
              emit={emit}
              showApplyAll={showApplyAll}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PotBlock({ pot, originalPot, onChange, emitForPot, scope, showCups, showApplyAll }) {
  const [open, setOpen] = useState(true);

  // Cihaz tarafından gelen type ("TFC"/"TC") ve type-lokal displayIndex
  const isTcByType = pot.type === "TC";
  const isTfcByType = pot.type === "TFC";

  // Bu pot'taki tum flat ayar alanlarini bucket'la
  const tfcKeys = [];
  const tcFlatKeys = [];
  for (const k of Object.keys(pot)) {
    if (!isSettingField(pot[k])) continue;
    if (TFC_FIELD_KEYS.has(k)) tfcKeys.push(k);
    else if (TC_FLAT_FIELD_KEYS.has(k)) tcFlatKeys.push(k);
    else {
      // Bilinmeyen alan — pot tipine göre yedir
      if (isTcByType || Array.isArray(pot.cups)) tcFlatKeys.push(k);
      else tfcKeys.push(k);
    }
  }
  const allTcCups = Array.isArray(pot.cups) ? pot.cups : [];
  // Basit kapsamda fincan ayarları yalnız ilk gözde düzenlenir, tüm gözlere
  // yazılır (tabletteki gibi göz seçici yok).
  const tcCups = showCups ? allTcCups : [];

  const total =
    tfcKeys.length +
    tcFlatKeys.length +
    tcCups.reduce((acc, cup) => {
      const modes = cup.modes || {};
      const cupLevel = isSettingField(cup.waterLevel) ? 1 : 0;
      const modeFields = Object.keys(modes).reduce(
        (a, m) =>
          a +
          Object.keys(modes[m] || {}).filter((k) => isSettingField(modes[m]?.[k]))
            .length,
        0,
      );
      return acc + cupLevel + modeFields;
    }, 0);

  // Pot turu — once cihazdan gelen pot.type, yoksa alan adlarindan tahmin
  const isTcPot =
    isTcByType || (!isTfcByType && (tcFlatKeys.length > 0 || tcCups.length > 0));
  const PotIcon = isTcPot ? FaMugHot : FaLeaf;
  const potTypeLabel = isTcPot ? "Türk Kahvesi" : "Çay-Filtre Kahve";
  // type-lokal displayIndex varsa kullan, yoksa raw index
  const headerNumber = pot.displayIndex ?? pot.index;

  // Renk: TFC → emerald (cay/yapraksi), TC → amber (kahve)
  const headerBg = isTcPot
    ? "bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100"
    : "bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100";
  const headerBorder = isTcPot ? "border-amber-200" : "border-emerald-200";
  const iconColor = isTcPot ? "text-amber-700" : "text-emerald-700";
  const titleColor = isTcPot ? "text-amber-900" : "text-emerald-900";
  const badgeStyle = isTcPot
    ? "bg-amber-100 text-amber-700 border-amber-200"
    : "bg-emerald-100 text-emerald-700 border-emerald-200";

  return (
    <div
      className={`rounded-xl border bg-white mb-3 last:mb-0 overflow-hidden ${headerBorder}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full px-3 py-2.5 flex items-center gap-2 text-left ${headerBg}`}
      >
        {open ? (
          <FaChevronDown className={iconColor} />
        ) : (
          <FaChevronRight className={iconColor} />
        )}
        <PotIcon className={iconColor} />
        <span className={`text-sm font-bold ${titleColor}`}>
          {headerNumber}. Göz {potTypeLabel}
        </span>
        <span
          className={`ml-auto text-[11px] font-medium px-2 py-0.5 rounded-md border ${badgeStyle}`}
        >
          {total} alan
        </span>
      </button>
      {open && (
        <div className="p-3 space-y-3 border-t border-gray-200">
          {tfcKeys.length > 0 && (
            <div>
              <SettingsSubgroupHeader
                icon={FaLeaf}
                title="Çay-Filtre Kahve"
                count={tfcKeys.length}
                color={COLOR}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {tfcKeys.map((k) => {
                  const setting = applyLabelOverride(k, pot[k]);
                  const orig = originalPot?.[k]?.value;
                  const isChanged =
                    orig !== undefined && !valuesEqual(setting.value, orig);
                  return (
                    <SettingsFieldCard
                      key={k}
                      setting={setting}
                      onChange={(value) =>
                        onChange({
                          group: "custom",
                          potIndex: pot.index,
                          type: pot.type,
                          subgroup: "teaFilterCoffee",
                          key: k,
                          value,
                        })
                      }
                      isChanged={isChanged}
                      originalValue={orig}
                      color={COLOR}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {(tcFlatKeys.length > 0 || tcCups.length > 0) && (
            <div>
              <SettingsSubgroupHeader
                icon={FaMugHot}
                title="Türk Kahvesi"
                count={tcFlatKeys.length + tcCups.length}
                color={COLOR}
              />
              {tcFlatKeys.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-3">
                  {tcFlatKeys.map((k) => {
                    const setting = applyLabelOverride(k, pot[k]);
                    const orig = originalPot?.[k]?.value;
                    const isChanged =
                      orig !== undefined && !valuesEqual(setting.value, orig);
                    return (
                      <SettingsFieldCard
                        key={k}
                        setting={setting}
                        onChange={(value) =>
                          onChange({
                            group: "custom",
                            potIndex: pot.index,
                            type: pot.type,
                            subgroup: "turkishCoffee",
                            key: k,
                            value,
                          })
                        }
                        isChanged={isChanged}
                        originalValue={orig}
                        color={COLOR}
                      />
                    );
                  })}
                </div>
              )}
              {tcCups.length > 0 && (
                <div>
                  <div className="text-[11px] font-bold text-amber-700 mb-1.5 uppercase tracking-wide">
                    Fincanlar ({tcCups.length})
                  </div>
                  {scope === "basit" && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mb-2">
                      Basit kapsam: buradaki fincan ayarları tüm gözlere ve
                      tüm pişirme modlarına birlikte yazılır.
                    </p>
                  )}
                  {scope === "standart" && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mb-2">
                      Standart kapsam: her değişiklik bu gözün üç pişirme
                      moduna (Hızlı / Normal / Gurme) birlikte yazılır.
                    </p>
                  )}
                  {tcCups.map((cup, ci) => (
                    <CupBlock
                      key={cup.id || ci}
                      cup={cup}
                      originalCup={
                        Array.isArray(originalPot?.cups)
                          ? originalPot.cups.find((c) => c.id === cup.id)
                          : null
                      }
                      potIndex={pot.index}
                      emit={emitForPot(pot)}
                      scope={scope}
                      showApplyAll={showApplyAll}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {!showCups && allTcCups.length > 0 && (
            <p className="text-xs text-gray-500 italic">
              Fincan ayarları Basit kapsamda ilk gözden düzenlenir.
            </p>
          )}
          {tfcKeys.length === 0 &&
            tcFlatKeys.length === 0 &&
            allTcCups.length === 0 && (
              <p className="text-xs text-gray-400 italic">
                Bu gözde ayarlanabilir alan yok.
              </p>
            )}
        </div>
      )}
    </div>
  );
}

// Pot tipini alan adlarindan tespit et (cihaz pot.type gondermiyorsa fallback)
const detectPotType = (pot) => {
  if (pot?.type === "TFC" || pot?.type === "TC") return pot.type;
  for (const k of Object.keys(pot)) {
    if (!isSettingField(pot[k])) continue;
    if (TFC_FIELD_KEYS.has(k)) return "TFC";
    if (TC_FLAT_FIELD_KEYS.has(k)) return "TC";
  }
  if (Array.isArray(pot?.cups)) return "TC";
  return "TFC";
};

const SCOPES = new Set(["basit", "standart", "gelismis"]);
const ALL_MODES = ["fast", "normal", "gourmet"];

export default function CustomPotSection({
  pots,
  onChange,
  originalPots,
  editScope,
}) {
  if (!Array.isArray(pots) || pots.length === 0) return null;

  // displayIndex'i client'ta hesapla — cihaz pot.displayIndex gondermese bile
  // her tip icin 1'den baslayan numara verir
  let tfcN = 0;
  let tcN = 0;
  const decoratedPots = pots.map((pot) => {
    const type = detectPotType(pot);
    const displayIndex =
      typeof pot.displayIndex === "number"
        ? pot.displayIndex
        : type === "TFC"
          ? ++tfcN
          : ++tcN;
    return { ...pot, type, displayIndex };
  });

  // Düzenleme kapsamı cihazdan (general.turkishCoffee.editScope). Eski
  // cihaz göndermiyorsa mod ayrımlı eski davranış (gelismis) korunur.
  const scope = SCOPES.has(editScope) ? editScope : "gelismis";
  const tcPots = decoratedPots.filter(
    (p) => p.type === "TC" && Array.isArray(p.cups),
  );
  const firstTcIndex = tcPots[0]?.index;

  // Fincan alanı değişikliğini kapsama göre genişletir:
  //  - mod: standart/basit → 3 mod, gelismis → yalnız o mod
  //  - göz: basit ya da "tüm gözlere uygula" → aynı fincanı taşıyan tüm
  //    Türk kahvesi gözleri, aksi halde yalnız bu göz
  const emitForPot =
    (pot) =>
    ({ cupId, mode, key, value }, { allPots = false } = {}) => {
      const targetPots =
        scope === "basit" || allPots
          ? tcPots.filter((p) => p.cups.some((c) => c.id === cupId))
          : [pot];
      const targetModes = !mode
        ? [undefined]
        : scope === "gelismis"
          ? [mode]
          : ALL_MODES;
      for (const tp of targetPots) {
        for (const m of targetModes) {
          onChange({
            group: "custom",
            potIndex: tp.index,
            type: tp.type,
            subgroup: "turkishCoffee",
            cupId,
            ...(m ? { mode: m } : {}),
            key,
            value,
          });
        }
      }
    };

  return (
    <SettingsSectionShell>
      {decoratedPots.map((pot, i) => (
        <PotBlock
          key={`${pot.type}-${pot.index ?? i}-${pot.displayIndex}`}
          pot={pot}
          originalPot={originalPots?.find(
            (p) =>
              p.index === pot.index &&
              (p.type === pot.type || detectPotType(p) === pot.type),
          )}
          onChange={onChange}
          emitForPot={emitForPot}
          scope={scope}
          showCups={scope !== "basit" || pot.index === firstTcIndex}
          showApplyAll={scope !== "basit" && tcPots.length > 1}
        />
      ))}
    </SettingsSectionShell>
  );
}
